import { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Group, Loader, Modal, SegmentedControl, Select, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  type CommitResponse,
  type PreviewResponse,
  type PreviewView,
  type SplitCandidate,
  type SplitCandidatesResponse,
  type SplitMode,
  type SplitParams
} from '@parts/shared';
import { errorText, getJson, postJson } from '../../api';
import { DayInput } from '../../components/DayInput';
import { FieldLabel } from '../../components/ui';
import { useI18n } from '../../i18n';
import { useAppState } from '../../state';
import { PreviewModal } from '../purchase/PreviewModal';

interface Props {
  opened: boolean;
  onClose: () => void;
  /** Запрос, выбранный на экране (подставляется в режиме «один запрос»). */
  initialReq?: string;
  canCommit: boolean;
}

const EMPTY: SplitParams = {
  mode: 'one',
  reqNo: '',
  newNo: '',
  deadline: '',
  comment: '',
  includeUnbought: false,
  includeRemoved: false
};

/**
 * «Закрыть по закупленному и перенести остаток»: в исходном запросе требуемое становится равным закупленному,
 * недокупленное — новым запросом. Сначала предпросмотр, затем проведение одной операцией.
 */
export function SplitDialog({ opened, onClose, initialReq, canCommit }: Props) {
  const { t, tx, tn } = useI18n();
  const { apply } = useAppState();
  const [params, setParams] = useState<SplitParams>(EMPTY);
  const [candidates, setCandidates] = useState<SplitCandidate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<{ view: PreviewView; version: number } | null>(null);
  const [committing, setCommitting] = useState(false);

  useEffect(() => {
    if (!opened) return;
    setCandidates(null);
    setError(null);
    getJson<SplitCandidatesResponse>('/api/split/candidates')
      .then((res) => {
        setCandidates(res.candidates);
        const pick = res.candidates.find((c) => c.no === initialReq)?.no ?? '';
        setParams({ ...EMPTY, reqNo: pick });
      })
      .catch((err) => setError(errorText(err)));
  }, [opened, initialReq]);

  const set = (patch: Partial<SplitParams>) => setParams((p) => ({ ...p, ...patch }));

  const label = (c: SplitCandidate) =>
    `№ ${c.no} — ${tn(c.positions, ['позиция', 'позиции', 'позиций'])}, ${c.qty} ${t('шт')}` +
    (c.removed ? ` · ${t('снятых: {n}', { n: c.removed })}` : '') +
    (c.hasBought ? '' : ` · ${t('без закупок')}`);

  const openPreview = async () => {
    setLoading(true);
    try {
      const res = await postJson<PreviewResponse>('/api/split/preview', params);
      if (res.ok) setPreview({ view: res.view, version: res.version });
      else notifications.show({ color: 'red', title: t('Нельзя перенести'), message: tx(res.message) });
    } catch (err) {
      notifications.show({ color: 'red', title: t('Ошибка'), message: tx(errorText(err)) });
    } finally {
      setLoading(false);
    }
  };

  const commit = async () => {
    if (!preview) return;
    setCommitting(true);
    try {
      const res = await postJson<CommitResponse>('/api/split/commit', { ...params, version: preview.version });
      if (!res.ok) {
        notifications.show({ color: 'red', title: t('Не проведено'), message: tx(res.message) });
        setPreview(null);
        return;
      }
      apply(res.state);
      setPreview(null);
      onClose();
      notifications.show({ color: 'green', title: t('Остаток перенесён'), message: tx(res.message), autoClose: 8000 });
    } catch (err) {
      notifications.show({ color: 'red', title: t('Ошибка'), message: tx(errorText(err)) });
    } finally {
      setCommitting(false);
    }
  };

  const list = candidates ?? [];
  const ready = params.newNo.trim() !== '' && (params.mode === 'all' || params.reqNo !== '');

  return (
    <>
      <Modal opened={opened && !preview} onClose={onClose} title={t('Закрыть по закупленному и перенести остаток')} size="lg">
        {error ? (
          <Alert color="red" variant="light">
            {tx(error)}
          </Alert>
        ) : !candidates ? (
          <Group gap={10} c="dimmed">
            <Loader size="sm" />
            {t('Загружаю запросы…')}
          </Group>
        ) : (
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              {t('В исходном запросе требуемое количество станет равным закупленному, а недокупленное попадёт в новый запрос. Закупки не меняются.')}
            </Text>
            <SegmentedControl
              value={params.mode}
              onChange={(v) => set({ mode: v as SplitMode })}
              data={[
                { value: 'one', label: t('Один запрос') },
                { value: 'all', label: t('Все открытые запросы') }
              ]}
            />
            {params.mode === 'one' ? (
              <Select
                label={t('Из какого запроса')}
                placeholder={list.length ? t('Выберите запрос') : t('Нет запросов с недокупленным')}
                value={params.reqNo || null}
                onChange={(v) => set({ reqNo: v ?? '' })}
                data={list.map((c) => ({ value: c.no, label: label(c) }))}
                searchable
                nothingFoundMessage={t('Не найдено')}
              />
            ) : (
              <Checkbox
                checked={params.includeUnbought}
                onChange={(e) => set({ includeUnbought: e.currentTarget.checked })}
                label={t('Включать запросы без закупок')}
                description={t('Такие запросы перейдут целиком, исходные станут «Отменено».')}
              />
            )}
            <Checkbox
              checked={params.includeRemoved}
              onChange={(e) => set({ includeRemoved: e.currentTarget.checked })}
              label={t('Включать позиции, снятые клиентом')}
              description={t('Вернутся в новый запрос в количестве до снятия. Каждая переносится только один раз.')}
            />
            <Group grow align="flex-start">
              <TextInput
                label={<FieldLabel text={t('№ нового запроса')} />}
                withAsterisk
                value={params.newNo}
                onChange={(e) => set({ newNo: e.target.value })}
              />
              <DayInput
                label={t('Срок поставки')}
                placeholder={t('дд.мм.гггг')}
                value={params.deadline}
                onChange={(v) => set({ deadline: v })}
              />
            </Group>
            <TextInput
              label={t('Комментарий')}
              placeholder={t('Пусто — «Остаток из запроса № …»')}
              value={params.comment}
              onChange={(e) => set({ comment: e.target.value })}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={onClose}>
                {t('Отмена')}
              </Button>
              <Button onClick={() => void openPreview()} loading={loading} disabled={!ready}>
                {t('Предпросмотр')}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <PreviewModal
        view={preview?.view ?? null}
        committing={committing}
        canCommit={canCommit}
        commitLabel={t('Закрыть и создать запрос')}
        onClose={() => setPreview(null)}
        onCommit={() => void commit()}
      />
    </>
  );
}

